<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { useRedirect } from "@core/composables"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { storeToRefs } from "pinia"
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            home: "Accueil",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    title: string
    icon?: string
    loading?: boolean
    /**
     * Redirection fil d’Ariane (router).
     * Fourni par HeaderPageBuilding ; défaut : useRedirect interne.
     */
    onNavigate?: (url: string) => void
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    icon: "",
    loading: false,
    onNavigate: undefined,
})

// ================================
// COMPOSABLES
// ================================

const buildingStore = useBuildingStore()

// ================================
// STATE
// ================================

const { building } = storeToRefs(buildingStore)
const { redirectWrapper } = useRedirect()

// ================================
// COMPUTED
// ================================

/**
 * Génère la structure du breadcrumb en fonction du bâtiment et du titre
 */
const getBreadCrumb = computed(() => {
    const breadCrumb: { icon: string; label: string; route?: string }[] = [
        { icon: "pi pi-home", label: t("home"), route: "/" },
    ]
    if (building.value?.folder) {
        breadCrumb.push({
            label: building.value?.folder.name || "",
            icon: "pi pi-folder-open",
            route: `/folder/${building.value?.folder._id_folder}`,
        })
    }
    breadCrumb.push({
        label: building.value?.name || "",
        icon: "pi pi-building",
    })
    breadCrumb.push({
        label: props.title,
        icon: props.icon,
    })
    return breadCrumb
})

// ================================
// INTERACTIONS
// ================================

/**
 * Quand on clique sur un élément du breadcrumb (accueil = `/`).
 * @param route - La route où naviguer
 */
function onClickNavigate(route: string): void {
    const navigate = props.onNavigate ?? redirectWrapper
    navigate(route)
}
</script>
<template>
    <!-- ================================ -->
    <!-- LOADING STATE -->
    <!-- ================================ -->
    <div
        v-if="loading"
        class="breadcrumb-building-loading flex items-center gap-2"
    >
        <BaseSkeleton width="20rem" height="1.5rem" />
    </div>

    <!-- ================================ -->
    <!-- BREADCRUMB -->
    <!-- ================================ -->
    <div
        v-else-if="getBreadCrumb.length >= 3"
        class="breadcrumb-building-wrapper overflow-x-auto overflow-y-hidden min-w-0"
    >
        <BaseBreadCrumb
            :model="getBreadCrumb"
            :home="null"
            class="breadcrumb-building text-blue-400"
        >
            <template #item="{ item, props: propsItem }">
                <div
                    class="breadcrumb-building__item flex items-center gap-1 md:gap-2 shrink-0 min-w-0 max-w-full md:max-w-none"
                >
                    <i
                        class="p-breadcrumb-item-icon shrink-0 text-sm md:text-base"
                        v-if="item.icon"
                        :class="item.icon"
                    />
                    <button
                        v-if="item.route"
                        type="button"
                        v-bind="propsItem.action"
                        @click.prevent="onClickNavigate(item.route)"
                        class="cursor-pointer min-w-0"
                    >
                        <span
                            class="hover:underline truncate max-w-[7rem] sm:max-w-[10rem] md:max-w-none block"
                        >
                            {{ item.label }}
                        </span>
                    </button>
                    <p
                        v-else
                        :href="item.url"
                        :target="item.target"
                        v-bind="propsItem.action"
                        class="min-w-0"
                    >
                        <span
                            class="truncate max-w-[7rem] sm:max-w-[10rem] md:max-w-none block"
                        >
                            {{ item.label }}
                        </span>
                    </p>
                </div>
            </template>
        </BaseBreadCrumb>
    </div>
</template>

<style scoped>
/* Fil d'Ariane responsive : pas de retour à la ligne, scroll horizontal sur mobile */
.breadcrumb-building-wrapper :deep(.p-breadcrumb-list) {
    flex-wrap: nowrap;
}

.breadcrumb-building-wrapper :deep(.p-breadcrumb-item) {
    flex-shrink: 0;
}

/* Scroll discret sur mobile (masque la scrollbar tout en gardant le scroll) */
@media (max-width: 767px) {
    .breadcrumb-building-wrapper {
        -webkit-overflow-scrolling: touch;
        scrollbar-width: thin;
    }
}
</style>
